import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  cartAffirmDataSelector,
  cartAllProductsSelector,
  cartAppliedCouponsSelector,
  cartIdSelector,
  cartPricesSelector,
  cartTaxesTotalAmountSelector,
  checkoutSelectedShippingMethodSelector,
  isApplePayAvailable,
  resolveCatchMessage,
  TCatchMessage,
  tcSchema,
  TTcSchema,
  useCheckoutAgreementsQuery,
  useLazyGetCustomerPaymentTokensQuery,
  useLoadingContext,
  useRecaptchaSettingQuery,
  userIsLoggedInSelector,
  useSetPaymentMethodAndPlaceOrderMutation,
  useToastContext,
} from '@segway/core'
import { PaymentMethodInput, ReCaptchaEnum } from '@segway/core/src/graphql/generated/graphql'
import { useAppSelector } from '@segway/core/src/store/hooks'
import { useDebounceFn } from 'ahooks'
import { useForm, useWatch } from 'react-hook-form'

import { GoogleReCaptchaRef } from '@/components/googleReCaptcha/GoogleReCaptcha'
import { useRouter } from '@/i18n/navigation'
import { generateGTMCartItemsParams } from '@/utils'
import reportGTMEvent from '@/utils/gtm'

type TMethod = { code: string; title: string } | undefined
type TVaultMethod = {
  code: string
  title: string
  public_hash: string
  cardType?: string
}

/**
 * Checkout page payment methods hooks
 */
const useCheckoutPaymentMethods = (methods: TMethod[] | undefined) => {
  const form = useForm<TTcSchema>({
    resolver: zodResolver(tcSchema),
    defaultValues: {
      checked: false,
    },
  })
  const loading = useLoadingContext()
  const toast = useToastContext()
  const router = useRouter()
  const cartAffirmData = useAppSelector(cartAffirmDataSelector)
  const cartPrices = useAppSelector(cartPricesSelector)
  const cartId = useAppSelector(cartIdSelector)
  const isLoggedIn = useAppSelector(userIsLoggedInSelector)
  const allProducts = useAppSelector(cartAllProductsSelector)
  const appliedCoupons = useAppSelector(cartAppliedCouponsSelector)
  const taxesTotalAmount = useAppSelector(cartTaxesTotalAmountSelector)
  const selectedShippingMethod = useAppSelector(checkoutSelectedShippingMethodSelector)

  const [getCustomerPaymentTokensQuery] = useLazyGetCustomerPaymentTokensQuery()
  const { data: checkoutAgreementData } = useCheckoutAgreementsQuery({})
  const { data: recaptchaData, isLoading: isRecaptchaLoading } = useRecaptchaSettingQuery({
    key: ReCaptchaEnum.PlaceOrder,
  })
  const [setPaymentMethodAndPlaceOrderMutation] = useSetPaymentMethodAndPlaceOrderMutation()

  const agreementRef = useRef<HTMLDivElement>(null)
  const googleReCaptchaRef = useRef<GoogleReCaptchaRef>(null)

  const [methodsList, setMethodsList] = useState<TMethod[] | undefined>([])
  const [vaultMethodsList, setVaultMethodsList] = useState<TVaultMethod[]>([])
  const [googleReCaptchaToken, setGoogleReCaptchaToken] = useState<string>('')
  const [googleReCaptchaLoaded, setGoogleReCaptchaLoaded] = useState(false)
  const [currentMethod, setCurrentMethod] = useState<string>('')
  const [publicHash, setPublicHash] = useState<string>('')

  // 使用 useWatch 监听表单 checked 数据变化
  const isTCChecked = useWatch({
    control: form.control,
    name: 'checked',
  })

  /**
   * 支付方式数据是否就绪
   */
  const methodDataReady = useMemo(() => {
    return methodsList && methodsList?.length > 0
  }, [methodsList])

  /**
   *  是否需要谷歌人机验证
   */
  const isGoogleRecaptchaInvisible = useMemo(() => {
    return recaptchaData?.recaptchaSetting?.invisible || false
  }, [recaptchaData])

  /**
   *  是否可下单(已勾选协议，已通过谷歌人机校验)
   */
  const placeOrderAvailable = useMemo(() => {
    if (isGoogleRecaptchaInvisible) {
      return !!(!isRecaptchaLoading && googleReCaptchaLoaded && googleReCaptchaToken && isTCChecked)
    }

    return !!isTCChecked
  }, [
    isTCChecked,
    isGoogleRecaptchaInvisible,
    googleReCaptchaToken,
    isRecaptchaLoading,
    googleReCaptchaLoaded,
  ])

  /**
   *  结算协议
   */
  const checkoutAgreement = useMemo(() => {
    return checkoutAgreementData?.checkoutAgreements?.[0]?.checkbox_text ?? ''
  }, [checkoutAgreementData])

  /**
   * 检查 google recaptcha 是否加载成功
   */
  const googleRecaptchaAsyncScriptOnLoad = (...rest: { loaded: boolean }[]) => {
    if (rest[0]?.loaded) {
      setGoogleReCaptchaLoaded(true)
    }
  }

  /**
   *  Select payment method
   */
  const selectMethod = useCallback((method: string) => {
    setCurrentMethod(method)
    setPublicHash('')
  }, [])

  /**
   *  Select payment method public hash
   */
  const selectVaultMethod = useCallback((method: string, hashVal: string) => {
    setCurrentMethod(method)
    setPublicHash(hashVal)
  }, [])

  /**
   *  检测是否已通过谷歌人机校验
   */
  const checkGoogleReCaptcha = useCallback(async () => {
    if (isGoogleRecaptchaInvisible) {
      const newGoogleReCaptchaToken = await googleReCaptchaRef.current?.executeAsync()
      if (newGoogleReCaptchaToken) {
        setGoogleReCaptchaToken(newGoogleReCaptchaToken)
        return
      }
    }
  }, [isGoogleRecaptchaInvisible])

  /**
   *  检测Google Pay服务是否可用
   */
  const checkGooglePayServiceAccessible = useCallback(async () => {
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => {
        // 超时，终止请求
        controller.abort()
      }, 5000)

      // 测试Google Pay核心域名
      const endpoints = [
        'https://pay.google.com/gp/p/ping',
        'https://payments.google.com/payments/apis/ping',
      ]
      const results = await Promise.all(
        endpoints.map((url) =>
          fetch(url, {
            method: 'HEAD',
            mode: 'no-cors',
            cache: 'no-store',
            signal: controller.signal,
          })
            .then(() => true)
            .catch(() => false),
        ),
      )
      clearTimeout(timeoutId)
      return results.some((result) => result)
    } catch (error) {
      console.error('Google Pay服务检测异常:', error)
      return false
    }
  }, [])

  /**
   *  创建支付方式列表, 检测支付方式是否可用，不可用则不会显示
   */

  const initSavedMethods = useCallback(async () => {
    try {
      if (!methods || methods?.length === 0) {
        return
      }
      // 是否有存储过的支付方式(信用卡、Paypal)
      const vaultMethods = methods.filter((method) => {
        return (
          method?.code && ['braintree_cc_vault', 'braintree_paypal_vault'].includes(method.code)
        )
      })
      if (vaultMethods.length > 0) {
        const res = await getCustomerPaymentTokensQuery({}).unwrap()
        if (res?.customerPaymentTokens && res?.customerPaymentTokens?.items?.length > 0) {
          const list: TVaultMethod[] = []
          res.customerPaymentTokens.items?.forEach((vaultItem) => {
            // Credit Card
            const isCreditCardVault =
              vaultItem?.type === 'card' && vaultItem?.payment_method_code === 'braintree'
            // Paypal
            const isPaypalVault = vaultItem?.payment_method_code === 'braintree_paypal'
            const findVaultMethod = (code: string) => {
              return vaultMethods.find((v) => v?.code === code)
            }
            // 保存的信用卡列表
            if (isCreditCardVault) {
              const creditCardVaultMethod = findVaultMethod('braintree_cc_vault')
              if (creditCardVaultMethod) {
                const details = vaultItem?.details ? JSON.parse(vaultItem?.details) : ''
                list.push({
                  code: creditCardVaultMethod.code,
                  title: `ending ${details?.maskedCC ?? ''} (expires: ${details?.expirationDate ?? ''})`,
                  cardType: details?.type ?? '',
                  public_hash: vaultItem.public_hash,
                })
              }
            } else if (isPaypalVault) {
              // 保存的Paypal
              const paypalVaultMethod = findVaultMethod('braintree_paypal_vault')
              if (paypalVaultMethod) {
                const details = vaultItem?.details ? JSON.parse(vaultItem?.details) : ''
                list.push({
                  code: paypalVaultMethod.code,
                  title: details?.payerEmail ?? '',
                  public_hash: vaultItem.public_hash,
                })
              }
            }
          })
          setVaultMethodsList(list)
        }
      }
    } catch (e) {
      toast.show({
        icon: 'fail',
        content: resolveCatchMessage(e as TCatchMessage),
      })
    }
  }, [getCustomerPaymentTokensQuery, methods, toast])

  /**
   *  创建支付方式列表, 检测支付方式是否可用，不可用则不会显示
   */
  const initMethods = useCallback(async () => {
    try {
      if (!methods || methods?.length === 0) {
        return
      }
      const availableList = [
        'checkmo',
        'braintree_paypal',
        'braintree',
        'affirm_gateway',
        'openpay_cards',
        // 'braintree_venmo', // TODO
      ]
      // 检测是否支持GooglePay
      const isGooglePayAvailable = await checkGooglePayServiceAccessible()
      if (isGooglePayAvailable) {
        availableList.push('braintree_googlepay')
      }
      // 检测是否支持ApplePage
      if (isApplePayAvailable()) {
        availableList.push('braintree_applepay')
      }
      setMethodsList(
        methods.filter((method) => {
          return method && availableList.includes(method?.code ?? '')
        }),
      )
    } catch (e) {
      toast.show({
        icon: 'fail',
        content: resolveCatchMessage(e as TCatchMessage),
      })
    }
  }, [checkGooglePayServiceAccessible, methods, toast])

  /**
   *  支付提交
   */
  const submitPlaceOrder = useCallback(
    async (paymentMethodQuery: PaymentMethodInput) => {
      try {
        reportGTMEvent({
          event: 'add_payment_info',
          ecommerce: {
            currency: cartPrices?.grand_total?.currency,
            value: cartPrices?.subtotal_excluding_tax?.value,
            payment_type: paymentMethodQuery,
            coupon: appliedCoupons && appliedCoupons?.length ? appliedCoupons[0]?.code : '',
            items: generateGTMCartItemsParams(allProducts),
          },
        })

        loading.show()
        const headers = googleReCaptchaToken
          ? {
              headers: {
                'X-ReCaptcha': googleReCaptchaToken,
              },
            }
          : {}
        const res = await setPaymentMethodAndPlaceOrderMutation({
          variables: {
            input: {
              cart_id: cartId,
              payment_method: paymentMethodQuery,
            },
          },
          ...headers,
        })
        loading.hide()
        if (res?.data?.setPaymentMethodAndPlaceOrder?.order?.order_id) {
          /**
           * GTM: purchase
           */
          reportGTMEvent({
            event: 'purchase',
            ecommerce: {
              transaction_id: res.data.setPaymentMethodAndPlaceOrder.order.order_id,
              currency: cartPrices?.grand_total?.currency,
              value: cartPrices?.grand_total?.value,
              tax: taxesTotalAmount,
              shipping: selectedShippingMethod?.amount.value,
              coupon: appliedCoupons && appliedCoupons.length ? appliedCoupons[0]?.code : '',
              items: generateGTMCartItemsParams(allProducts),
            },
          })

          router.replace(
            `/checkout/success/${res.data.setPaymentMethodAndPlaceOrder.order.order_id}`,
          )
        } else if (res?.error) {
          toast.show({
            icon: 'fail',
            content: resolveCatchMessage(res.error as TCatchMessage),
          })
        }
      } catch (e) {
        toast.show({
          icon: 'fail',
          content: resolveCatchMessage(e as TCatchMessage),
        })
      }
    },
    [
      cartId,
      googleReCaptchaToken,
      loading,
      router,
      setPaymentMethodAndPlaceOrderMutation,
      toast,
      allProducts,
      cartPrices,
      appliedCoupons,
      taxesTotalAmount,
      selectedShippingMethod,
    ],
  )

  /**
   *  使用已保存的卡、Paypal支付
   */
  const { run: vaultPlaceOrder } = useDebounceFn(
    async () => {
      submitPlaceOrder({
        code: currentMethod,
        [currentMethod]: {
          public_hash: publicHash,
        },
      })
    },
    {
      wait: 500,
    },
  )

  /**
   *  checkmo支付
   */
  const { run: checkmoPlaceOrder } = useDebounceFn(
    async () => {
      submitPlaceOrder({
        code: 'checkmo',
      })
    },
    {
      wait: 500,
    },
  )

  useEffect(() => {
    initMethods()
  }, [initMethods])

  useEffect(() => {
    if (isLoggedIn) {
      initSavedMethods()
    }
  }, [initSavedMethods, isLoggedIn])

  const { run: handleCheckBefore } = useDebounceFn(
    () => {
      if (agreementRef?.current?.scrollIntoView) {
        agreementRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      }
      // 检查是否验证谷歌人机校验
      checkGoogleReCaptcha()
      // 检查协议是否勾选
      form.handleSubmit(() => {})()
    },
    {
      wait: 500,
    },
  )

  /**
   *  OpenPay Payment
   */
  const { run: openPayPlaceOrder } = useDebounceFn(
    async (tokenId, deviceId, installments) => {
      try {
        loading.show()
        console.log('openPayPlaceOrder cart id', cartId)
        const res = await setPaymentMethodAndPlaceOrderMutation({
          variables: {
            input: {
              cart_id: cartId,
              payment_method: {
                code: 'openpay_cards',
                additional_data: {
                  openpay_token: tokenId,
                  device_session_id: deviceId,
                  interest_free: installments,
                },
              },
            },
          },
        })
        loading.hide()
        console.log('Place order successful, res: ', res)
        if (res?.data?.setPaymentMethodAndPlaceOrder?.order?.order_id) {
          router.replace(
            `/checkout/success/${res.data.setPaymentMethodAndPlaceOrder.order.order_id}`,
          )
        } else if (res?.error) {
          toast.show({
            icon: 'fail',
            content: resolveCatchMessage(res.error as TCatchMessage),
          })
        }
      } catch (e) {
        loading.hide()
        toast.show({
          icon: 'fail',
          content: resolveCatchMessage(e as TCatchMessage),
        })
      }
    },
    {
      wait: 500,
    },
  )

  return {
    form,
    publicHash,
    cartPrices,
    methodsList,
    agreementRef,
    selectMethod,
    recaptchaData,
    currentMethod,
    cartAffirmData,
    methodDataReady,
    vaultPlaceOrder,
    vaultMethodsList,
    selectVaultMethod,
    handleCheckBefore,
    checkoutAgreement,
    checkmoPlaceOrder,
    openPayPlaceOrder,
    googleReCaptchaRef,
    placeOrderAvailable,
    googleReCaptchaToken,
    setGoogleReCaptchaToken,
    isGoogleRecaptchaInvisible,
    googleRecaptchaAsyncScriptOnLoad,
  }
}

export default useCheckoutPaymentMethods
